import * as Sentry from '@sentry/react'

import { logger } from 'utils/logging'

/**
 * Guards React's DOM reconciliation against the `NotFoundError` that browser
 * auto-translation (Google Translate / DeepL) triggers.
 *
 * The problem (ISSUE-2153, recurrence of ISSUE-1309 / FRONTEND-PLATFORM-66SD):
 * when a page is auto-translated, the browser swaps text nodes for `<font>`
 * wrappers *outside* React's knowledge. React's virtual DOM still points at the
 * original nodes, so when it later unmounts a subtree (e.g. our `PageTransition`
 * removing the outgoing view after an exit animation) it calls
 * `parent.removeChild(node)` on a node that is no longer a child of `parent` —
 * the DOM throws `NotFoundError: Failed to execute 'removeChild' on 'Node'` and
 * the whole render crashes to a white screen.
 *
 * Our `PageTransition` already wraps the GSAP enter/exit timelines in
 * `try/catch` + `document.contains`, but that crash originates in React's own
 * commit phase (`react-dom`), not in the timelines — so those guards never see
 * it. The only place to intercept React's internal mutation is the DOM API
 * itself.
 *
 * This is the React-team-acknowledged workaround (facebook/react#11538): patch
 * `Node.prototype.removeChild` / `insertBefore` so that, instead of throwing
 * when the parent/reference relationship is already broken, they degrade to a
 * no-op and return the node — exactly the outcome React expected. In every
 * normal case the native implementation runs untouched.
 */

let installed = false

/**
 * Idempotently patch `Node.prototype.removeChild` and `Node.prototype.insertBefore`
 * to swallow the cross-parent `NotFoundError` instead of crashing the render.
 *
 * Call once, as early as possible in the bootstrap — before React mounts — so
 * the guard is active for the very first reconciliation.
 */
export function installSafeDomMutations(): void {
  if (installed) return
  if (typeof Node !== 'function' || !Node.prototype) return

  installed = true

  const reportSwallowedMutation = (operation: 'removeChild' | 'insertBefore') => {
    // Breadcrumb only (not an event) so a translated session that later hits a
    // real error carries the context, without flooding Sentry on every guard hit.
    Sentry.addBreadcrumb({
      category: 'dom.safe-mutation',
      level: 'warning',
      message: `Swallowed cross-parent ${operation} (likely browser auto-translation)`,
    })

    // Console-only in dev for local visibility; never its own Sentry event.
    logger.warn(
      `Swallowed cross-parent ${operation} to avoid a DOM NotFoundError`,
      undefined,
      { sentry: false, context: { component: 'safeDomMutations', action: operation } }
    )
  }

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportSwallowedMutation('removeChild')
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportSwallowedMutation('insertBefore')
      return newNode
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T
  }
}
